import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./primitives";
import { Zap, Sparkles, Scissors, Activity, ArrowRight } from "lucide-react";

const IMG = {
  approccio: "https://images.pexels.com/photos/5846885/pexels-photo-5846885.jpeg?auto=compress&cs=tinysrgb&w=1100",
  laser: "https://images.unsplash.com/photo-1655841439659-0afc60676b70?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  skin: "https://images.unsplash.com/photo-1710580889701-9fa8f2cd5927?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  tricologia: "https://images.unsplash.com/photo-1662559385121-899d8a9993a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  corpo: "https://images.pexels.com/photos/16408396/pexels-photo-16408396.jpeg?auto=compress&cs=tinysrgb&w=1100",
  tecnologie: "https://images.pexels.com/photos/1954/black-and-white-lights-abstract-curves.jpg?auto=compress&cs=tinysrgb&w=1100",
  doctor: "/dott-valle.png",
  staff: "/team-hands.jpg",
};

function ParallaxImage({ src, alt, className = "", gray = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className={`absolute inset-0 w-full h-[116%] object-cover ${gray ? "grayscale-[20%]" : ""}`} />
      <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
    </div>
  );
}

function Chips({ items, dark = false }) {
  return (
    <div className="mt-8 flex flex-wrap gap-2.5">
      {items.map((t, i) => (
        <Reveal key={t} delay={i * 0.03} className={`border px-4 py-2 text-[13px] transition-colors duration-300 ${dark ? "border-canvas/25 text-canvas/70 hover:border-canvas hover:text-canvas" : "border-ink/15 text-ink/70 hover:border-sage hover:text-ink"}`}>
          {t}
        </Reveal>
      ))}
    </div>
  );
}

/* ---------- APPROCCIO ---------- */
export function Approccio() {
  return (
    <section id="approccio" data-testid="section-approccio" className="py-24 md:py-36 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal><Overline>Un approccio diverso</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
            La medicina estetica non deve necessariamente modificare i volumi del volto.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">
            È possibile lavorare sulla qualità della pelle, sulla luminosità, sull’elasticità
            e sull’uniformità dei tessuti attraverso tecnologie avanzate e trattamenti
            rigenerativi selezionati in base alle caratteristiche del paziente.
          </Reveal>
          <Reveal delay={0.28} as="p" className="mt-5 max-w-2xl text-base text-muted leading-relaxed">
            L’attività del Dott. Alessandro Valle si concentra su trattamenti laser,
            tecnologie energy-based, tricologia e iniettivi dedicati prevalentemente alla
            skin quality. All’interno della struttura, il percorso può essere completato da
            uno staff dedicato alle tecnologie per il corpo.
          </Reveal>
          <Reveal delay={0.35} as="p" className="mt-8 font-serif italic text-xl text-graphite">
            Migliorare la qualità dei tessuti senza trasformare l’identità della persona.
          </Reveal>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2">
          <ParallaxImage src={IMG.approccio} alt="Valutazione della pelle" className="h-[50vh] lg:h-[70vh] w-full" />
        </div>
      </div>
    </section>
  );
}

/* ---------- QUATTRO PILASTRI ---------- */
export function Pilastri() {
  const pillars = [
    { icon: Zap, n: "01", t: "Tecnologie Laser", d: "Precisione, selettività e conoscenza dei tessuti. La tecnologia scelta in funzione del bersaglio biologico.", to: "laser" },
    { icon: Sparkles, n: "02", t: "Skin Quality", d: "Medicina rigenerativa per migliorare la pelle senza modificare i lineamenti.", to: "skinquality" },
    { icon: Scissors, n: "03", t: "Tricologia", d: "Percorsi personalizzati per il diradamento e la qualità dei capelli.", to: "tricologia" },
    { icon: Activity, n: "04", t: "Corpo e tecnologie", d: "Comprendere quale tessuto crea il problema prima di scegliere il trattamento.", to: "corpo" },
  ];
  return (
    <section id="pilastri" data-testid="section-pilastri" className="py-24 md:py-32 bg-graphite text-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Overline className="text-canvas/70">Quattro aree principali</Overline></Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-canvas/15 border border-canvas/15">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.n} delay={i * 0.08} className="bg-graphite">
                <button
                  data-testid={`pillar-${p.to}`}
                  onClick={() => document.getElementById(p.to)?.scrollIntoView({ behavior: "smooth" })}
                  className="text-left w-full h-full p-8 md:p-10 group hover:bg-[#34383a] transition-colors duration-500"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-6 h-6 text-sage" strokeWidth={1.3} />
                    <span className="font-serif text-2xl text-canvas/25">{p.n}</span>
                  </div>
                  <h3 className="mt-8 font-serif text-2xl md:text-3xl font-light">{p.t}</h3>
                  <p className="mt-4 text-sm text-canvas/55 leading-relaxed">{p.d}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-sage group-hover:gap-3 transition-[gap] duration-300">
                    Scopri <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- LASER ---------- */
export function Laser() {
  const factors = ["Tipo di lesione", "Profondità", "Colore e diametro del bersaglio", "Fototipo", "Sede anatomica", "Trattamenti precedenti", "Tempi di recupero", "Obiettivo clinico"];
  const indications = ["Cicatrici da acne", "Cicatrici chirurgiche e traumatiche", "Macchie e discromie", "Capillari, angiomi e lesioni vascolari", "Fotoinvecchiamento", "Rughe superficiali", "Pori dilatati", "Texture irregolare", "Lassità iniziale", "Tatuaggi", "Epilazione", "Resurfacing ablativo e non ablativo"];
  return (
    <section id="laser" data-testid="section-laser" className="py-24 md:py-40 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <Reveal><Overline>Tecnologie Laser</Overline></Reveal>
            <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Precisione, selettività e conoscenza dei tessuti.
            </Reveal>
            <Reveal delay={0.2} as="p" className="mt-7 max-w-lg text-lg text-muted leading-relaxed">
              Non esiste un unico laser adatto a ogni problema. Ogni lunghezza d’onda
              interagisce in maniera differente con i principali bersagli della pelle:
              <span className="text-ink"> acqua, melanina, emoglobina.</span>
            </Reveal>
            <Reveal delay={0.28} as="p" className="mt-5 max-w-lg text-base text-muted leading-relaxed">
              Anche durata dell’impulso, fluenza, spot, profondità, densità e raffreddamento
              modificano il risultato. La tecnologia non viene scelta in base al macchinario,
              ma in funzione di:
            </Reveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              {factors.map((f, i) => (
                <Reveal key={f} delay={i * 0.04} className="flex items-center gap-3 border-t border-ink/10 py-3 text-[15px] text-ink/80">
                  <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>{f}
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <ParallaxImage src={IMG.laser} alt="Trattamento laser" className="h-[46vh] lg:h-[58vh] w-full" />
            <Reveal delay={0.1} as="p" className="mt-8 text-[11px] uppercase tracking-[0.2em] text-muted">Principali indicazioni</Reveal>
            <Chips items={indications} />
            <Reveal delay={0.2} as="p" className="mt-8 font-serif italic text-lg text-graphite">
              Il trattamento nasce dalla comprensione del problema, non dall’utilizzo di una piattaforma.
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SKIN QUALITY ---------- */
export function SkinQuality() {
  const goals = ["Idratazione", "Luminosità", "Texture", "Compattezza", "Elasticità", "Rughe fini", "Matrice extracellulare"];
  const treatments = ["Biorivitalizzazione", "Skinbooster non volumizzanti", "Polinucleotidi", "PRP", "Peeling medicali", "Laser ablativi e non ablativi", "Radiofrequenza", "Ultrasuoni focalizzati", "Protocolli combinati"];
  return (
    <section id="skinquality" data-testid="section-skinquality" className="py-24 md:py-40 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <ParallaxImage src={IMG.skin} alt="Skin quality" className="h-[56vh] lg:h-[76vh] w-full" />
        </div>
        <div className="lg:col-span-7">
          <Reveal><Overline>Skin Quality & medicina rigenerativa</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Migliorare la pelle senza modificare i lineamenti.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 max-w-xl text-lg text-muted leading-relaxed">
            L’obiettivo non è creare nuovi volumi, ma migliorare idratazione, luminosità,
            texture, compattezza ed elasticità, e la capacità della pelle di recuperare
            e mantenersi nel tempo.
          </Reveal>
          <Chips items={goals} />
          <Reveal delay={0.15} as="p" className="mt-10 text-[11px] uppercase tracking-[0.2em] text-muted">I percorsi possono integrare</Reveal>
          <Chips items={treatments} />
          <Reveal delay={0.2} as="p" className="mt-10 font-serif italic text-xl text-graphite max-w-lg">
            L’obiettivo non è cambiare i lineamenti, ma migliorare la qualità della pelle che li riveste.
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- TRICOLOGIA ---------- */
export function Tricologia() {
  const evaluation = ["Tipo di diradamento", "Durata del problema", "Familiarità", "Miniaturizzazione del capello", "Velocità di progressione", "Stato del cuoio capelluto", "Carenze o condizioni sistemiche", "Trattamenti già effettuati"];
  const path = ["Valutazione clinica e anamnestica", "Documentazione fotografica", "Esami ematochimici, quando indicati", "Terapia domiciliare personalizzata", "Integratori specifici", "PRP", "Hair filler con peptidi biomimetici", "Trattamenti di mantenimento", "Supporto prima o dopo un trapianto"];
  return (
    <section id="tricologia" data-testid="section-tricologia" className="py-24 md:py-40 bg-graphite text-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <Reveal><Overline className="text-canvas/70">Tricologia</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Percorsi personalizzati per il diradamento e la qualità dei capelli.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 max-w-xl text-lg text-canvas/60 leading-relaxed">
            La caduta e il diradamento non dovrebbero essere trattati applicando lo stesso
            protocollo a ogni paziente. Prima di proporre PRP, hair filler o integratori è
            importante comprendere:
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {evaluation.map((f, i) => (
              <Reveal key={f} delay={i * 0.04} className="flex items-center gap-3 border-t border-canvas/15 py-3 text-[15px] text-canvas/80">
                <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>{f}
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} as="p" className="mt-10 font-serif italic text-lg text-canvas/90 max-w-lg">
            L’obiettivo non è promettere una ricrescita uguale per tutti, ma selezionare il paziente e costruire un percorso coerente con la diagnosi.
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <ParallaxImage src={IMG.tricologia} alt="Tricologia" className="h-[46vh] lg:h-[52vh] w-full" gray />
          <Reveal delay={0.1} as="p" className="mt-8 text-[11px] uppercase tracking-[0.2em] text-canvas/50">Il percorso può comprendere</Reveal>
          <Chips items={path} dark />
        </div>
      </div>
    </section>
  );
}

/* ---------- CORPO ---------- */
export function Corpo() {
  const targets = ["Cellulite e PEFS", "Ritenzione ed edema", "Adiposità localizzate", "Lassità", "Qualità della pelle del corpo", "Rimodellamento", "Compattezza post-dimagrimento", "Microcircolo e texture"];
  const tech = ["Radiofrequenza", "Onde d’urto", "Vacuum", "Ultrasuoni", "Tecnologie di rimodellamento", "Trattamenti iniettivi selezionati", "Protocolli combinati"];
  return (
    <section id="corpo" data-testid="section-corpo" className="py-24 md:py-40 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Overline>Corpo e tecnologie</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Comprendere quale tessuto crea il problema.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 text-lg text-muted leading-relaxed">
            Cellulite, adiposità localizzata, edema, lassità e perdita di compattezza non
            sono sinonimi e non possono essere trattati con lo stesso protocollo. I percorsi
            vengono costruiti dopo una valutazione che distingue le diverse componenti.
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <ParallaxImage src={IMG.corpo} alt="Percorsi corpo" className="h-[46vh] lg:h-[56vh] w-full" />
          </div>
          <div className="lg:col-span-6">
            <Reveal as="p" className="text-[11px] uppercase tracking-[0.2em] text-muted">Lo staff si occupa di</Reveal>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              {targets.map((f, i) => (
                <Reveal key={f} delay={i * 0.04} className="flex items-center gap-3 border-t border-ink/10 py-3 text-[15px] text-ink/80">
                  <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>{f}
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1} as="p" className="mt-8 text-[11px] uppercase tracking-[0.2em] text-muted">Tecnologie</Reveal>
            <Chips items={tech} />
          </div>
        </div>
        <Reveal delay={0.1} as="div" className="mt-14 border-l-2 border-sage pl-6 max-w-3xl">
          <p className="text-base text-muted leading-relaxed">
            Ogni trattamento viene affidato al professionista più adatto in base alle
            competenze richieste. Il lavoro dello staff non consiste nell’applicare lo stesso
            macchinario a ogni paziente, ma nel combinare valutazione, tecnologia, manualità
            e controlli nel tempo.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- TECNOLOGIE ---------- */
export function Tecnologie() {
  const items = [
    { t: "Laser CO₂", d: "Interagisce con l’acqua. Modalità ablativa o frazionata per cicatrici, texture, rughe superficiali, pori, resurfacing." },
    { t: "Er:YAG 2940 nm", d: "Fortemente assorbito dall’acqua. Ablazione precisa, resurfacing, cicatrici, modalità termiche subablative." },
    { t: "Frazionati non ablativi", d: "Colonne di danno termico senza rimuovere l’epidermide. Cicatrici, pori, texture, rimodellamento dermico." },
    { t: "Laser vascolari", d: "532 nm per vasi superficiali; 1064 nm per bersagli profondi. Fotocoagulazione controllata secondo il vaso." },
    { t: "Q-switched · pigmento", d: "Impulsi brevissimi per rimozione tatuaggi e frammentazione selettiva del pigmento." },
    { t: "Luce pulsata", d: "Ampio spettro. Fotoinvecchiamento, pigmentazioni superficiali, rossore diffuso, epilazione." },
    { t: "Radiofrequenza", d: "Riscaldamento controllato dei tessuti. Lassità, compattezza, qualità cutanea, rimodellamento." },
    { t: "RF frazionata con aghi", d: "Trauma controllato + energia nel derma. Cicatrici da acne, texture, pori, rughe, lassità." },
    { t: "Ultrasuoni focalizzati", d: "Energia concentrata a profondità prestabilite. Compattezza, lassità, rimodellamento su piani diversi." },
  ];
  return (
    <section id="tecnologie" data-testid="section-tecnologie" className="py-24 md:py-40 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <Reveal><Overline>Tecnologie</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Tecnologie differenti per bersagli differenti.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 text-lg text-muted leading-relaxed">
            Ogni piattaforma viene scelta in funzione del bersaglio, della profondità e della
            risposta biologica che si desidera ottenere.
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={(i % 3) * 0.06} className="bg-sand p-8 group hover:bg-stone transition-colors duration-500">
              <span className="font-serif text-sage text-xl">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-5 font-serif text-2xl leading-tight">{it.t}</h3>
              <p className="mt-3 text-sm text-muted leading-relaxed">{it.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} as="p" className="mt-10 max-w-3xl text-base text-muted leading-relaxed border-l-2 border-sage pl-6">
          La presenza di una tecnologia non significa che sia indicata per tutti. La scelta
          tiene conto di fototipo, sede, stagione, profondità del bersaglio, tempi di recupero,
          precedenti trattamenti, rischio pigmentario e risposta individuale.
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- DOTT. VALLE ---------- */
export function Doctor() {
  const focus = ["Laser ablativi e non ablativi", "Laser vascolari", "Trattamenti per il pigmento", "Cicatrici da acne, chirurgiche e traumatiche", "Resurfacing e fotoringiovanimento", "Texture, pori, lassità", "Radiofrequenza e ultrasuoni", "Skinbooster, polinucleotidi, PRP", "Tricologia e diradamento"];
  return (
    <section id="dottore" data-testid="section-doctor" className="py-24 md:py-40 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <ParallaxImage src={IMG.doctor} alt="Dott. Alessandro Valle" className="h-[62vh] lg:h-[86vh] w-full" gray />
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">Dott. Alessandro Valle · Medico</p>
        </div>
        <div className="lg:col-span-7 lg:pt-4">
          <Reveal><Overline>Il medico</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Dott. Alessandro Valle
          </Reveal>
          <Reveal delay={0.15} as="p" className="mt-6 max-w-2xl text-lg text-muted leading-relaxed">
            Laureato in Medicina e Chirurgia presso l’Università Vita-Salute San Raffaele di
            Milano, con una tesi sperimentale dedicata alle reazioni avverse cutanee gravi ai
            farmaci — un’analisi clinica, istopatologica e immunofenotipica che ha
            approfondito la risposta della pelle ai danni e ai meccanismi di rigenerazione.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-5 max-w-2xl text-base text-muted leading-relaxed">
            Ha scelto di concentrare la propria attività sulla medicina estetica tecnologica:
            fisica dei laser, interazione tra energia e tessuti, selezione delle lunghezze
            d’onda e costruzione di protocolli personalizzati. Prosegue la formazione presso
            la Scuola Agorà di Milano ed è socio della Società Italiana di Medicina Estetica.
            Ha approfondito le applicazioni laser anche presso la sede Fotona di Lubiana.
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            {focus.map((f, i) => (
              <Reveal key={f} delay={i * 0.04} className="flex items-start gap-3 border-t border-ink/10 py-3.5 text-[15px] text-ink/80">
                <span className="text-sage font-serif text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span>{f}
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} as="div" className="mt-10 bg-graphite text-canvas p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-sage">Approccio non volumetrico</p>
            <p className="mt-4 text-lg leading-relaxed text-canvas/85">
              L’attività non è costruita sulla trasformazione sistematica dei lineamenti
              attraverso filler volumetrici, ma sul miglioramento progressivo di pelle,
              texture, cicatrici, discromie, compattezza e qualità dei capelli. Gli iniettivi
              hanno soprattutto finalità di skin quality e biostimolazione.
            </p>
            <p className="mt-6 font-serif italic text-xl text-canvas">
              La tecnologia è efficace solo quando si comprende quale tessuto trattare e quale risposta biologica ottenere.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- METODO (5 fasi) ---------- */
export function Metodo() {
  const steps = [
    { n: "01", t: "Valutazione", d: "Analisi della storia clinica, della condizione da trattare, dei trattamenti precedenti e delle aspettative." },
    { n: "02", t: "Definizione dell’obiettivo", d: "Individuazione delle priorità e spiegazione dei risultati realisticamente ottenibili." },
    { n: "03", t: "Scelta della tecnologia", d: "Selezione della lunghezza d’onda, del dispositivo o del trattamento rigenerativo più coerente." },
    { n: "04", t: "Trattamento personalizzato", d: "Adattamento di parametri, tecnica, numero di sedute e intervalli alle caratteristiche individuali." },
    { n: "05", t: "Follow-up", d: "Controllo della risposta e modifica progressiva del percorso, quando necessario." },
  ];
  return (
    <section id="metodo" data-testid="section-metodo" className="py-24 md:py-40 bg-graphite text-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <Reveal><Overline className="text-canvas/70">Il metodo</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Prima il problema, poi la tecnologia.
          </Reveal>
          <Reveal delay={0.2} as="p" className="mt-7 text-lg text-canvas/60 leading-relaxed">
            Non sempre il trattamento inizialmente richiesto è quello più indicato. Una macchia
            può essere epidermica o dermica; una lassità legata a pelle, grasso o volume; un
            diradamento di origine diversa. La valutazione serve a distinguere prima di scegliere.
          </Reveal>
        </div>
        <div className="divide-y divide-canvas/15 border-y border-canvas/15">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05} data-testid={`method-step-${i + 1}`}>
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-8 md:py-11 items-baseline hover:px-4 transition-[padding] duration-500">
                <span className="md:col-span-3 font-serif text-5xl md:text-7xl font-light text-canvas/20 group-hover:text-sage transition-colors duration-500">{s.n}</span>
                <h3 className="md:col-span-3 font-serif text-2xl md:text-3xl">{s.t}</h3>
                <p className="md:col-span-6 text-base md:text-lg text-canvas/60 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- STUDIO E STAFF ---------- */
export function Staff() {
  const doctor = ["Valutazione medica", "Trattamenti laser", "Skin quality", "Tricologia", "Procedure rigenerative", "Protocolli tecnologici avanzati"];
  const team = ["Corpo", "Cellulite", "Edema e microcircolo", "Lassità", "Rimodellamento", "Qualità dei tessuti", "Supporto domiciliare", "Follow-up dei programmi"];
  return (
    <section id="staff" data-testid="section-staff" className="py-24 md:py-40 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-14">
          <div className="lg:col-span-6">
            <Reveal><Overline>Studio e Staff</Overline></Reveal>
            <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Competenze differenti, un unico percorso.
            </Reveal>
            <Reveal delay={0.2} as="p" className="mt-7 max-w-lg text-lg text-muted leading-relaxed">
              La struttura nasce dalla collaborazione tra professionisti con competenze
              differenti. Ogni procedura viene affidata al professionista più appropriato e
              inserita all’interno di un percorso condiviso.
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <ParallaxImage src={IMG.staff} alt="Studio a Genova" className="h-[40vh] lg:h-[46vh] w-full" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          <Reveal className="bg-canvas p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted">Dott. Alessandro Valle</p>
            <Chips items={doctor} />
          </Reveal>
          <Reveal delay={0.08} className="bg-canvas p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted">Staff dedicato</p>
            <Chips items={team} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- LONGEVITÀ (ridimensionata) ---------- */
export function Longevita() {
  const areas = ["Nutrizione", "Metabolismo", "Composizione corporea", "Prevenzione", "Salute ormonale", "Benessere generale"];
  return (
    <section id="longevita" data-testid="section-longevita" className="py-20 md:py-28 bg-stone">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal><Overline>Una visione più ampia</Overline></Reveal>
          <Reveal delay={0.1} as="h2" className="mt-6 font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight">
            Una visione più ampia della cura di sé.
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal as="p" className="text-base text-muted leading-relaxed max-w-2xl">
            La qualità della pelle è influenzata anche da alimentazione, metabolismo, sonno,
            ormoni, stress e composizione corporea. Il progetto nasce con l’obiettivo di
            integrare progressivamente medicina estetica, prevenzione e longevità all’interno
            di una rete multidisciplinare. Quando necessario il percorso può essere condiviso con professionisti dedicati a:
          </Reveal>
          <Chips items={areas} />
        </div>
      </div>
    </section>
  );
}

/* ---------- PERCHÉ ---------- */
export function Perche() {
  const points = [
    { t: "Valutazione medica", d: "Prima del trattamento si analizza la condizione di partenza e si escludono situazioni da approfondire." },
    { t: "Più tecnologie disponibili", d: "Non viene utilizzata la stessa piattaforma per ogni indicazione." },
    { t: "Parametri personalizzati", d: "Energia, durata dell’impulso, profondità e tecnica adattate al tessuto e al paziente." },
    { t: "Risultati progressivi", d: "Molti trattamenti richiedono più sedute. Non si promettono risultati immediati o identici per tutti." },
    { t: "Approccio non trasformativo", d: "Migliorare la qualità dei tessuti preservando identità ed espressività." },
    { t: "Follow-up", d: "Il percorso non termina con la seduta: la risposta viene controllata e il programma modificato." },
  ];
  return (
    <section data-testid="section-perche" className="py-24 md:py-36 bg-canvas">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Overline>Perché questo approccio</Overline></Reveal>
        <Reveal delay={0.1} as="h2" className="mt-7 max-w-3xl font-serif font-light text-4xl md:text-5xl leading-[1.08] tracking-tight">
          Tecnologia, personalizzazione e naturalezza.
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-2">
          {points.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.05} className="border-t border-ink/15 py-7">
              <span className="font-serif text-2xl text-sage">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-serif text-xl text-ink">{p.t}</h3>
              <p className="mt-2 text-sm text-muted leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CHIUSURA ---------- */
export function Closing({ onBook }) {
  return (
    <section data-testid="section-closing" className="py-24 md:py-36 bg-sand">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <Reveal><Overline className="justify-center">Prenota</Overline></Reveal>
        <Reveal delay={0.1} as="h2" className="mt-7 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          Il trattamento corretto nasce da una valutazione corretta.
        </Reveal>
        <Reveal delay={0.2} as="p" className="mt-8 text-lg text-muted leading-relaxed max-w-2xl mx-auto">
          La prima valutazione serve a comprendere il problema, definire gli obiettivi e
          scegliere la tecnologia o il percorso più coerente. Laser, trattamenti rigenerativi,
          tricologia e tecnologie per il corpo offrono risultati importanti se inseriti in una
          strategia personalizzata.
        </Reveal>
        <Reveal delay={0.3} as="div" className="mt-10">
          <button
            data-testid="closing-book-button"
            onClick={onBook}
            className="group relative inline-flex items-center overflow-hidden border border-ink px-10 py-4 text-xs uppercase tracking-[0.2em]"
          >
            <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
          </button>
          <p className="mt-6 text-sm text-muted">Studio medico a Genova.</p>
        </Reveal>
      </div>
    </section>
  );
}
